import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  ButtonComp,
  CardComp,
  TextInputComp,
  FormikTextAreaComp,
} from "../../../components";
import { PageTitle } from "../modules/PageHeaders";
import {
  useGetSupportSettingsQuery,
  useUpdateSupportSettingsMutation,
} from "../../../store/admin/settings/supportSettingsSlice";
import { toast } from "react-hot-toast";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import MemberReferralStaffSection from "./MemberReferralStaffSection";

const phoneRegExp = /^([0]{1})[0-9]{10}$/;

const SupportSettings = () => {
  const { data: settings, isLoading } = useGetSupportSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateSupportSettingsMutation();

  const validationSchema = Yup.object({
    support_email: Yup.string()
      .email("Please enter a valid email address")
      .required("Support email is required"),
    support_phone: Yup.string()
      .matches(phoneRegExp, "Phone number must be 11 digits starting with 0")
      .required("Support phone number is required"),
    support_instructions: Yup.string(),
  });

  const handleSubmit = async (values) => {
    try {
      const response = await updateSettings(values).unwrap();
      if (response?.status === true) {
        toast.success(response?.message || "Support settings updated successfully");
      } else {
        toast.error(response?.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating support settings:", error);
      toast.error(error?.data?.message || "An error occurred while updating settings");
    }
  };

  if (isLoading) {
    return (
      <section className="px-3 px-lg-5 min-vh-100 pb-5 mb-5">
        <PageTitle title="Support Settings" />
        <CardComp cardClassName="mt-4">
          <TableSkeletonLoader rows={5} columns={1} />
        </CardComp>
      </section>
    );
  }

  const initialValues = {
    support_email: settings?.support_email ?? "",
    support_phone: settings?.support_phone ?? "",
    support_instructions: settings?.support_instructions ?? "",
  };

  return (
    <section className="px-3 px-lg-5 min-vh-100 pb-5 mb-5">
      <div className="mt-1">
        <PageTitle title="Support Settings" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting: formikSubmitting }) => {
          const isFormLoading = isUpdating || formikSubmitting;

          return (
            <Form className="mt-4">
              <div className="space-y-4">
                <CardComp cardClassName="rounded-4 shadow3 border-0" data-tour="staff-support-form">
                  <h5 className="fw-bold text-black2 mb-3">Support Contact Information</h5>
                  <p className="text-blacklight mb-4 small">
                    Configure the contact details shown to members on the Support page and to
                    investors on opportunity detail pages (Need help). If phone and email are not
                    set here, investors will not see a Need help section.
                  </p>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <TextInputComp
                        name="support_email"
                        LabelText={"Support Email"}
                        LabelClassName={"fw-2"}
                        placeholder={"Enter support email address"}
                        borderColor="#A1A0A0"
                        required
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <TextInputComp
                        name="support_phone"
                        LabelText={"Support Phone Number"}
                        LabelClassName={"fw-2"}
                        placeholder={"Enter phone number (11 digits starting with 0)"}
                        borderColor="#A1A0A0"
                        required
                      />
                    </div>

                    <div className="col-12">
                      <FormikTextAreaComp
                        fieldName="support_instructions"
                        LabelText={"Support Instructions"}
                        labelClassName={"fw-2"}
                        rows={6}
                        placeholder={"Enter instructions for members on how to apply for support (optional)"}
                      />
                      <p className="text-blacklight mt-2 small">
                        This text will be displayed to members on the Support page. You can include information about
                        office hours, response times, or any specific procedures members should follow.
                      </p>
                    </div>
                  </div>
                </CardComp>

                {/* Save Button */}
                <div className="d-flex justify-content-center pt-4" data-tour="staff-support-save">
                  <ButtonComp
                    btnText={isFormLoading ? "Saving..." : "Save Settings"}
                    btnClassName="bg-primary1 text-white px-5 py-3 rounded-10 border-0 shadow"
                    type="submit"
                    disabled={isFormLoading}
                    ButtonInlineStyle={{ minWidth: "250px" }}
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <MemberReferralStaffSection />
    </section>
  );
};

export default SupportSettings;
